/* memo-chip.js
   The 9x9 memo quick-add chip: ONE resolver + ONE state machine for both chip
   kinds (theme#493 extraction; theme#488 parity).

     [data-dev-swatchchip]  the fabric-card chip (snippets/dev-product-card.liquid);
                            its colorway = the card's ACTIVE swatch dot
     [data-cw-swatch]       the collection-products chip (sections/collection-products.liquid);
                            its colorway = data-color (fixed per block)

   Both carry data-fabric-handle and, once the catalog owner has synced the
   fabric, data-memo-map (theme#489):
     product.metafields.asthetik.memo_map = { "<colorway>": { handle, variantId, sku }, ... }
   listing ONLY the colorways with a purchasable memo. Map present -> the verdict
   is decided here with ZERO requests ({} = synced, no memos). Map absent (fabric
   not yet synced) -> the /products/9x9-memo-*.js probe ladder below is the
   fallback, as before.

   State law (theme#487/#488, Rule #138 spirit): a control that promises "Add a
   swatch" never vanishes. A missing memo turns the chip into an honest, disabled
   "No swatch" word for that colorway (card chip: .dev-product-card__swatchchip--none;
   collection chip: .is-none) and flips back to "+" when a colorway that has one is
   active. A cart error (stock cap, transient) is NOT "no swatch": the control is
   handed back so the shopper can retry. Existence is pre-resolved on intent
   (hover / focus / first touch) and on every colorway change so the word is
   usually decided BEFORE the click; the click path stays honest for the races.

   Why one document-level listener: sections/dev-collection-grid.liquid rebuilds
   its grid via innerHTML on every filter/sort, and the plains page runs ten
   collection-products instances -- delegation on `document` needs no re-init.
   Loaded (defer) by every section that renders either chip; the wire test
   (scripts/test-memo-map.mjs) asserts that pairing and drives this file's real
   entry points (#223).

   window.AsthetikMemo exposes the resolver + decide() for dev-product-card.js
   (colorway toggle -> re-decide) and any future consumer (the PDP sample
   buttons, theme#493 follow-up). */
(function () {
  'use strict';
  if (window.__asthetikMemoChipInit) return; // guard: the module is loaded by several sections
  window.__asthetikMemoChipInit = true;

  var resolved = {}; // fabric:colorway -> { variantId } | { missing: true } (+ fromMap)
  var pending = {};  // in-flight lookups, so hover chatter + a click never double-fetch

  var KIND_CARD = { noneClass: 'dev-product-card__swatchchip--none', sourceId: 'dev-card-swatch' };
  var KIND_CW = { noneClass: 'is-none', sourceId: 'collection-products-swatch' };
  function kindOf(btn) { return btn.hasAttribute('data-cw-swatch') ? KIND_CW : KIND_CARD; }

  /* ---------- resolver ---------- */
  function handleize(str) {
    return String(str).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function memoCandidates(fabric, color) {
    var list = ['9x9-memo-' + fabric + '-' + handleize(color)];
    var squashed = '9x9-memo-' + fabric + '-' + handleize(color.replace(/\s+/g, ''));
    if (list.indexOf(squashed) === -1) list.push(squashed);
    return list;
  }
  function parseMemoMap(raw) {
    if (raw == null || raw === '') return null;
    var map = null;
    try { map = JSON.parse(raw); } catch (e) { return null; }
    return (map && typeof map === 'object' && !Array.isArray(map)) ? map : null;
  }
  function memoMapOf(btn) {
    if (!btn || !btn.hasAttribute('data-memo-map')) return null;
    if (btn.__memoMap === undefined) btn.__memoMap = parseMemoMap(btn.getAttribute('data-memo-map'));
    return btn.__memoMap;
  }
  // Exact colorway key first, then the handleized form -- the producer keys by
  // the fabric's Color option value; the casing/spacing drift between systems
  // is the same drift the handle ladder tolerates.
  function memoMapLookup(map, color) {
    if (!map || !color) return null;
    if (Object.prototype.hasOwnProperty.call(map, color)) return map[color];
    var want = handleize(color);
    var keys = Object.keys(map);
    for (var i = 0; i < keys.length; i++) {
      if (handleize(keys[i]) === want) return map[keys[i]];
    }
    return null;
  }
  function cacheKey(fabric, color) { return fabric + ':' + String(color).toLowerCase(); }
  // map: the parsed data-memo-map (or null). With a map the answer is decided
  // here with no request; without one the handle ladder probes /products/*.js.
  function resolve(fabric, color, map) {
    var key = cacheKey(fabric, color);
    if (resolved[key]) return Promise.resolve(resolved[key]);
    if (pending[key]) return pending[key];
    var handles;
    if (map) {
      var entry = memoMapLookup(map, color);
      var vid = entry && (entry.variantId || entry.variant_id);
      if (vid) {
        resolved[key] = { variantId: vid, fromMap: true };
        return Promise.resolve(resolved[key]);
      }
      if (!entry || !entry.handle) {
        resolved[key] = { missing: true, fromMap: true };
        return Promise.resolve(resolved[key]);
      }
      handles = [String(entry.handle)]; // an entry with a handle but no id: probe that ONE handle, not the ladder
    } else {
      handles = memoCandidates(fabric, color);
    }
    function attempt(i) {
      if (i >= handles.length) return Promise.resolve({ missing: true });
      return fetch('/products/' + handles[i] + '.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) {
          if (!r.ok) return attempt(i + 1);
          return r.json().then(function (p) {
            var vs = p.variants || [];
            var v = vs.filter(function (x) { return x.available; })[0];
            return v ? { variantId: v.id } : attempt(i + 1);
          });
        })
        .catch(function () { return attempt(i + 1); });
    }
    pending[key] = attempt(0).then(function (res) {
      resolved[key] = res;
      delete pending[key];
      return res;
    });
    return pending[key];
  }
  // Forget a cached verdict and re-resolve through the LIVE probe ladder (no
  // map) -- the stale-map escape hatch: a memo re-created or drafted since the
  // last sync can make a map-sourced variant id one the cart refuses.
  function refresh(fabric, color) {
    delete resolved[cacheKey(fabric, color)];
    return resolve(fabric, color, null);
  }
  function addToCart(variantId, sourceId) {
    var src = sourceId || 'memo-chip';
    var data = new FormData();
    data.append('id', variantId);
    data.append('quantity', 1);
    return fetch('/cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: data })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        if (json && json.status && json.message) return { error: true };
        document.dispatchEvent(new CustomEvent('cart:update', {
          detail: { resource: null, sourceId: src, data: { source: src, itemCount: 1 } }
        }));
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (window.theme && typeof window.theme.refreshCart === 'function') window.theme.refreshCart();
        return json;
      });
  }

  /* ---------- chip state ---------- */
  function activeColorOf(card) {
    if (!card) return '';
    var active = card.querySelector('[data-card-swatch].dev-product-card__swatch--active') ||
      card.querySelector('[data-card-swatch]');
    return (active && active.getAttribute('data-color-name')) || '';
  }
  // The colorway a chip answers for: data-color when fixed (collection chip),
  // else the card's active dot (card chip).
  function colorOf(btn) {
    if (!btn) return '';
    if (btn.hasAttribute('data-color')) return btn.getAttribute('data-color') || '';
    return activeColorOf(btn.closest('[data-product-card]'));
  }
  function chipOfCard(card) {
    return card ? card.querySelector('[data-dev-swatchchip]') : null;
  }
  // Remember the chip's rendered label + aria-label once, so every state change
  // restores the Liquid-authored words rather than re-inventing them in JS.
  function rememberChip(btn) {
    if (!btn.hasAttribute('data-chip-label')) {
      btn.setAttribute('data-chip-label', btn.textContent);
      btn.setAttribute('data-chip-aria', btn.getAttribute('aria-label') || '');
    }
  }
  function setChipReady(btn) {
    rememberChip(btn);
    btn.hidden = false;
    btn.disabled = false;
    btn.classList.remove(kindOf(btn).noneClass, 'is-added');
    btn.__tickColor = null;
    btn.textContent = btn.getAttribute('data-chip-label');
    var aria = btn.getAttribute('data-chip-aria');
    if (aria) btn.setAttribute('aria-label', aria);
    btn.removeAttribute('title');
  }
  function setChipNone(btn, color) {
    rememberChip(btn);
    btn.hidden = false;
    btn.disabled = true;
    btn.classList.remove('is-added');
    btn.__tickColor = null;
    btn.classList.add(kindOf(btn).noneClass);
    btn.textContent = 'No swatch';
    var words = color ? 'No swatch available in ' + color : 'No swatch available';
    btn.setAttribute('aria-label', words);
    btn.setAttribute('title', words);
  }
  // The added tick names the colorway it was added for; it yields to a
  // colorway CHANGE (honesty) but not to hover chatter, then re-decides.
  function markChipAdded(btn, color) {
    btn.classList.add('is-added');
    btn.__tickColor = color;
    btn.textContent = '✓';
    setTimeout(function () {
      if (!btn.classList.contains('is-added')) return; // already re-decided
      setChipReady(btn);
      decide(btn);
    }, 2600);
  }
  function applyVerdict(btn, color, res) {
    res.missing ? setChipNone(btn, color) : setChipReady(btn);
  }
  // Is the chip busy with something the intent path must not interrupt? An
  // add in flight always holds; the added tick holds for its own colorway but
  // yields to a colorway change (a tick for the wrong colorway is a lie).
  function busy(btn) {
    if (btn.classList.contains('is-added')) return btn.__tickColor === colorOf(btn);
    return btn.disabled && !btn.classList.contains(kindOf(btn).noneClass); // mid-add
  }
  // Decide the chip for its CURRENT colorway without a click: cached verdicts
  // apply synchronously; unknown ones resolve async and only apply if the chip
  // is still on the same colorway, and idle, when the answer lands.
  function decide(btn) {
    if (!btn || busy(btn)) return;
    var color = colorOf(btn);
    if (!color) return;
    var fabric = btn.getAttribute('data-fabric-handle');
    var cached = resolved[cacheKey(fabric, color)];
    if (cached) { applyVerdict(btn, color, cached); return; }
    resolve(fabric, color, memoMapOf(btn)).then(function (res) {
      if (colorOf(btn) !== color || busy(btn)) return;
      applyVerdict(btn, color, res);
    });
  }

  /* ---------- events ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[data-dev-swatchchip]') || t.closest('[data-cw-swatch]');
    if (!btn || btn.disabled) return;

    // The card chip sits inside the card's <a>; the collection chip is a
    // positioned sibling of its card anchor. Either way the click adds a
    // swatch and never opens the product page.
    e.preventDefault();
    e.stopPropagation();

    var kind = kindOf(btn);
    // No colorway to answer for (a card with no named dot) -> nothing to resolve; say so.
    var color = colorOf(btn);
    if (!color) { setChipNone(btn, ''); return; }

    rememberChip(btn);
    btn.disabled = true;
    btn.textContent = '…';
    var fabric = btn.getAttribute('data-fabric-handle');
    resolve(fabric, color, memoMapOf(btn))
      .then(function (res) {
        if (res.missing) { setChipNone(btn, color); return null; }
        return addToCart(res.variantId, kind.sourceId).then(function (result) {
          if (!(result && result.error)) { markChipAdded(btn, color); return null; }
          // A cart error is NOT "no swatch": hand the control back so the
          // shopper can retry, never hide it. A map-sourced id the cart
          // refuses may be a stale map: re-resolve ONCE through the live
          // ladder; a different purchasable variant -> add that; none -> the
          // honest word; the same id -> hand the control back.
          if (!res.fromMap) { setChipReady(btn); return null; }
          return refresh(fabric, color).then(function (fresh) {
            if (fresh.missing) { setChipNone(btn, color); return null; }
            if (fresh.variantId === res.variantId) { setChipReady(btn); return null; }
            return addToCart(fresh.variantId, kind.sourceId).then(function (again) {
              if (again && again.error) { setChipReady(btn); return; }
              markChipAdded(btn, color);
            });
          });
        });
      })
      .catch(function () { setChipReady(btn); })
      .then(function () {
        // The colorway may have changed under a slow add (card chip): the chip
        // answers for the CURRENT one.
        if (colorOf(btn) !== color) decide(btn);
      });
  });

  // Pre-resolve on intent (desktop hover / keyboard focus / first touch) so the
  // chip is usually decided before the click. Delegated; per fabric+colorway
  // the verdict is cached and in-flight lookups are shared, so repeat intents
  // cost nothing. A card's intent is the whole card; a collection card's is its
  // wrapper ([data-cw-card]) or the chip itself.
  function onIntent(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[data-dev-swatchchip]') || t.closest('[data-cw-swatch]');
    if (!btn) {
      var card = t.closest('[data-product-card]');
      if (card) btn = chipOfCard(card);
    }
    if (!btn) {
      var wrap = t.closest('[data-cw-card]');
      if (wrap) btn = wrap.querySelector('[data-cw-swatch]');
    }
    if (btn) decide(btn);
  }
  document.addEventListener('mouseover', onIntent);
  document.addEventListener('focusin', onIntent);
  document.addEventListener('touchstart', onIntent, { passive: true });

  window.AsthetikMemo = {
    handleize: handleize,
    candidates: memoCandidates,
    parseMemoMap: parseMemoMap,
    lookup: memoMapLookup,
    resolve: resolve,
    refresh: refresh,
    addToCart: addToCart,
    decide: decide,
    decideCard: function (card) { decide(chipOfCard(card)); }
  };
})();
